import React, { createContext, Dispatch, useContext, useReducer } from "react";
import {
  AssetOrLapsesChartData,
  CountChartData,
  CTmVehicleData,
  ODCState,
  RfIdScannedData,
  RoutesSeverityChartData,
  SectorSeverityChart,
  SensorDevice,
  SeverityPercent,
} from "../Models/Models";
import dayjs, { Dayjs } from "dayjs";
import moment from "moment";

const timeVariable = parseFloat(
  `${moment().format("H")}.${moment().format("mm")}`
);

const getTimeRange = () => {
  if (timeVariable == 21 || timeVariable > 21 || timeVariable < 4.1) {
    return "1";
  } else if (timeVariable == 4 || timeVariable < 11) {
    return "2";
  } else if (timeVariable == 11 || timeVariable < 16) {
    return "3";
  } else {
    return "4";
  }
};

export interface CtmLiveOverViewType {
  SeverityPercent: SeverityPercent;
  SelectedODC: string;
  selectedTime: string;
  SensorCriticalCount: number;
  CriticalCount: number;
  SensorDevices: SensorDevice[];
  StreetDataTableResponse: CountChartData[];
  RoutesSeverityChartData: RoutesSeverityChartData[];
  AssetCountChartData: AssetOrLapsesChartData[];
  LapsesCountChartData: AssetOrLapsesChartData[];

  LoMapload: boolean;
  LoMapviewState: {
    latitude: number;
    longitude: number;
    zoom: number;
  };
  Lomap: mapboxgl.Map | null;
  LoMapCleanData: number[][];
  LoMapModerateData: number[][];
  LoMapCriticalData: number[][];
  LoMapFullCleanData: number[][][];
  LoMapFullModerateData: number[][][];
  LoMapFullCriticalData: number[][][];
  LoMapReset: boolean;
  LoMapGetPopUpInfo: CTmVehicleData[];
  LoMapShowPopup: boolean;
  LoMapPopUpDetails: CTmVehicleData | null;
  LoMapOpen: boolean;
  LoMapFeatureData: any;
  LoMapOdcTime: ODCState;

  RfIdMapload: boolean;
  RfIdMaplShowPopup: boolean;
  RfIdMaplPopUpDetails: RfIdScannedData | null;
  RfIdMaplTruckTracker: [number, number][];
  RfIdMaplBinTracker: RfIdScannedData[];
  RfIdMaplRealTimeData: [number, number][];
  RfIdMaplWebsocketBinData: RfIdScannedData[];
  isWebSocketConneted: boolean;
}

const initialState: CtmLiveOverViewType = {
  SeverityPercent: {} as SeverityPercent,
  SelectedODC: "odcai_track2",
  selectedTime: getTimeRange(),
  SensorCriticalCount: 0,
  CriticalCount: 0,
  SensorDevices: [] as SensorDevice[],
  StreetDataTableResponse: [] as CountChartData[],
  RoutesSeverityChartData: [] as RoutesSeverityChartData[],
  AssetCountChartData: [] as AssetOrLapsesChartData[],
  LapsesCountChartData: [] as AssetOrLapsesChartData[],

  LoMapload: true,
  LoMapviewState: {
    latitude: 1.3179623906210907,
    longitude: 103.80952185215926,
    zoom: 12,
  },
  Lomap: null,
  LoMapCleanData: [],
  LoMapModerateData: [],
  LoMapCriticalData: [],
  LoMapFullCleanData: [],
  LoMapFullModerateData: [],
  LoMapFullCriticalData: [],
  LoMapReset: false,
  LoMapGetPopUpInfo: [],
  LoMapShowPopup: false,
  LoMapPopUpDetails: null,
  LoMapOpen: false,
  LoMapFeatureData: null,
  LoMapOdcTime: {
    odc: "odcai_track2",
    time: "1",
    state: true,
  },

  RfIdMapload: true,
  RfIdMaplShowPopup: false,
  RfIdMaplPopUpDetails: null,
  RfIdMaplTruckTracker: [],
  RfIdMaplBinTracker: [] as RfIdScannedData[],
  RfIdMaplRealTimeData: [],
  RfIdMaplWebsocketBinData: [] as RfIdScannedData[],
  isWebSocketConneted: true,
};

type ActionType =
  | {
      field: keyof CtmLiveOverViewType;
      type: "SET_FIELD";
      value:
        | string
        | string[]
        | boolean
        | number
        | HTMLElement
        | dayjs.Dayjs
        | { lat: number; lng: number }
        | mapboxgl.Map
        | { lat: number; lng: number }[]
        | AssetOrLapsesChartData[]
        | RoutesSeverityChartData[]
        | CTmVehicleData[]
        | CTmVehicleData
        | CountChartData[]
        | RfIdScannedData[]
        | RfIdScannedData
        | [number, number][]
        | number[][]
        | null;
    }
  | {
      type: "BULK_UPDATE";
      value: Partial<CtmLiveOverViewType>;
    }
  | {
      type: "RESET";
    };

const CtmLiveOverViewProvider = createContext<{
  ctmLiveOverViewState: CtmLiveOverViewType;
  dispatchCtmLiveOverView: Dispatch<ActionType>;
}>({
  ctmLiveOverViewState: initialState,
  dispatchCtmLiveOverView: () => null,
});

type ContextProviderProps = {
  children: React.ReactNode;
};

const reducer = (
  state: CtmLiveOverViewType,
  action: ActionType
): CtmLiveOverViewType => {
  switch (action.type) {
    case "SET_FIELD":
      return { ...state, [action.field]: action.value };
    case "BULK_UPDATE":
      return { ...state, ...action.value };
    case "RESET":
      return { ...initialState };

    default:
      return state;
  }
};

const CtmLiveOverViewContext = ({ children }: ContextProviderProps) => {
  const [ctmLiveOverViewState, dispatchCtmLiveOverView] = useReducer(
    reducer,
    initialState
  );

  return (
    <CtmLiveOverViewProvider.Provider
      value={{ ctmLiveOverViewState, dispatchCtmLiveOverView }}
    >
      {children}
    </CtmLiveOverViewProvider.Provider>
  );
};

const useCtmLiveOverView = () => {
  return useContext(CtmLiveOverViewProvider);
};

export { CtmLiveOverViewContext, useCtmLiveOverView };
